import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Users, RefreshCw, UserPlus, UserMinus, Search } from "lucide-react";

const BranchAgents = () => {
  const [agents, setAgents] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/branch/agents`);
      setAgents(res.data.agents || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load agents");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailable = async () => {
    try {
      // Fetch all users with role agent that are NOT in any branch (or in a different branch)
      const res = await axios.get(`${API}/admin/users`);
      const users = res.data.users || res.data || [];
      const inBranchIds = new Set(agents.map((a) => a.user_id));
      const candidates = users.filter(
        (u) => ["agent", "admin"].includes(u.role) && !inBranchIds.has(u.user_id)
      );
      setAvailable(candidates);
    } catch (e) {
      toast.error("Failed to load available agents");
    }
  };

  const openAssign = async () => {
    await fetchAvailable();
    setShowAssign(true);
  };

  const handleAssign = async (userId) => {
    try {
      await axios.post(`${API}/branch/agents/${userId}/assign`);
      toast.success("Agent assigned");
      setShowAssign(false);
      fetchAgents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign");
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm("Remove this agent from your branch?")) return;
    try {
      await axios.delete(`${API}/branch/agents/${userId}`);
      toast.success("Agent removed");
      fetchAgents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const filtered = available.filter(
    (u) =>
      !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branch-agents-page">
      <div className="flex justify-between items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold">My Team</h1>
          <p className="text-muted-foreground text-sm">
            Manage agents allocated to your branch
          </p>
        </div>
        <Button onClick={openAssign} data-testid="add-agent-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Assign Agent
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            {agents.length} Agent{agents.length !== 1 ? "s" : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {agents.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              No agents in this branch yet. Click "Assign Agent" to add one.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3">Agent</th>
                    <th className="text-center p-3">Properties</th>
                    <th className="text-center p-3">Deals</th>
                    <th className="text-center p-3">Won</th>
                    <th className="text-right p-3">Revenue</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a) => (
                    <tr key={a.user_id} className="border-b hover:bg-gray-50" data-testid={`agent-row-${a.user_id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <img
                            src={a.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(a.name)}&size=32`}
                            alt={a.name}
                            className="w-8 h-8 rounded-full"
                          />
                          <div>
                            <div className="font-medium">{a.name}</div>
                            <div className="text-xs text-muted-foreground">{a.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-center">{a.stats?.properties ?? 0}</td>
                      <td className="p-3 text-center">{a.stats?.total_deals ?? 0}</td>
                      <td className="p-3 text-center text-green-600 font-medium">{a.stats?.won_deals ?? 0}</td>
                      <td className="p-3 text-right font-semibold">
                        K {(a.stats?.revenue ?? 0).toLocaleString()}
                      </td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemove(a.user_id)}
                          data-testid={`remove-${a.user_id}`}
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign modal */}
      {showAssign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Assign Agent to Branch</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                <Input
                  placeholder="Search agents by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="agent-search"
                />
              </div>
              <div className="max-h-96 overflow-y-auto space-y-2">
                {filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No agents available to assign
                  </p>
                ) : (
                  filtered.map((u) => (
                    <div
                      key={u.user_id}
                      className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={u.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name)}&size=32`}
                          alt={u.name}
                          className="w-8 h-8 rounded-full"
                        />
                        <div>
                          <div className="font-medium">{u.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {u.email} · {u.role}
                            {u.branch_id && (
                              <span className="ml-2 text-amber-600">
                                (in another branch — will reassign)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => handleAssign(u.user_id)} data-testid={`assign-${u.user_id}`}>
                        Assign
                      </Button>
                    </div>
                  ))
                )}
              </div>
              <div className="flex justify-end pt-3 border-t">
                <Button variant="outline" onClick={() => setShowAssign(false)}>
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BranchAgents;
